<template>
    <b-modal
        :id="id"
        ref="modalRef"
        data-test="modal-platform-link"
        :data-step="currentStep"
        size="lg"
        centered
        hide-footer
        no-close-on-backdrop
        header-class="border-bottom py-3 px-4 bg-light"
        body-class="p-4"
        @hidden="onHidden"
    >
        <template #modal-title>
            <div class="d-flex align-items-center">
                <div class="platform-icon-circle mr-3" :class="form.platformType === 0 ? 'bg-light border shadow-sm' : ('bg-' + platformThemeColor)">
                    <i :class="[platformIconClass, form.platformType === 0 ? '' : 'text-white']"></i>
                </div>
                <div>
                    <h5 class="mb-0 font-weight-bold text-dark">
                        {{ isEditing ? 'Edit Platform Connection' : 'Add Cloud Platform Connection' }}
                    </h5>
                    <div class="small text-muted">
                        Authenticate a cloud platform once, then attach projects and repositories to it.
                    </div>
                </div>
            </div>
        </template>

        <b-form @submit.prevent="onFormSubmit">
            <!-- 2-Step Stepper Indicator -->
            <div class="wizard-stepper mb-4 d-flex justify-content-between align-items-center" data-test="stepper-platform-link">
                <div
                    class="wizard-step"
                    :class="{ 'active': currentStep === 1, 'completed': currentStep > 1 }"
                    role="button"
                    data-test="step-indicator-1"
                    @click="goToStep(1)"
                >
                    <div class="step-icon">
                        <i v-if="currentStep > 1" class="bi bi-check-lg"></i>
                        <span v-else>1</span>
                    </div>
                    <div class="step-label">Platform &amp; Details</div>
                </div>
                <div class="wizard-step-line" :class="{ 'completed': currentStep > 1 }"></div>
                <div
                    class="wizard-step"
                    :class="{ 'active': currentStep === 2, 'completed': form.isVerified }"
                    role="button"
                    data-test="step-indicator-2"
                    @click="canProceedToStep2 && goToStep(2)"
                >
                    <div class="step-icon">
                        <i v-if="form.isVerified && currentStep === 2" class="bi bi-check-lg"></i>
                        <span v-else>2</span>
                    </div>
                    <div class="step-label">Authentication &amp; Test</div>
                </div>
            </div>

            <!-- ==================== STEP 1: PLATFORM & DETAILS ==================== -->
            <div v-if="currentStep === 1" data-test="step-platform-details">
            <!-- 1. Platform Type Selection (only when adding new, or tabbed) -->
            <div class="mb-4" v-if="!isEditing">
                <label class="font-weight-bold text-dark small text-uppercase tracking-wider mb-2 d-block">
                    1. Select Platform
                </label>
                <b-row class="no-gutters">
                    <b-col cols="6" md="3" v-for="plat in platformOptions" :key="plat.type" class="p-1">
                        <div
                            class="platform-select-card text-center p-3 rounded border h-100 position-relative"
                            :class="{
                                'is-selected': form.platformType === plat.type,
                                'is-disabled': !plat.isAvailable
                            }"
                            role="button"
                            data-test="card-platform"
                            :data-test-id="plat.type"
                            :aria-pressed="form.platformType === plat.type ? 'true' : 'false'"
                            @click="plat.isAvailable && setPlatformType(plat.type)"
                        >
                            <div class="platform-card-icon mb-2">
                                <i :class="plat.icon" class="fa-2x"></i>
                            </div>
                            <div class="font-weight-bold small text-dark">{{ plat.name }}</div>
                            <div class="text-muted extra-small mt-1">{{ plat.subtitle }}</div>
                            <span v-if="!plat.isAvailable" class="badge badge-secondary coming-soon-pill">Soon</span>
                            <span v-if="form.platformType === plat.type" class="badge badge-primary check-pill">
                                <i class="bi bi-check-lg"></i>
                            </span>
                        </div>
                    </b-col>
                </b-row>
            </div>

            <!-- 2. Connection Details -->
            <div class="mb-4">
                <label class="font-weight-bold text-dark small text-uppercase tracking-wider mb-2 d-block">
                    {{ isEditing ? 'Connection Details' : '2. Connection Details' }}
                </label>

                <b-row>
                    <b-col md="6">
                        <b-form-group
                            :label="tenantLabel"
                            label-for="link-slug"
                            :description="tenantDescription"
                        >
                            <b-form-input
                                id="link-slug"
                                v-model.trim="form.tenantSlug"
                                required
                                data-test="input-link-slug"
                                :placeholder="tenantPlaceholder"
                                @input="onTenantSlugInput"
                            ></b-form-input>
                        </b-form-group>
                    </b-col>

                    <b-col md="6">
                        <b-form-group
                            label="Display Name"
                            label-for="link-name"
                            description="Friendly label to identify this connection in VelocyTrack."
                        >
                            <b-form-input
                                id="link-name"
                                v-model.trim="form.name"
                                required
                                data-test="input-link-name"
                                :disabled="!useCustomName"
                                :placeholder="useCustomName ? 'E.g. My ' + currentPlatformName : (form.tenantSlug || 'Auto-filled from organisation')"
                            ></b-form-input>
                            <b-form-checkbox v-model="useCustomName" class="mt-2 text-muted small" data-test="chk-custom-link-name">
                                Use different display name
                            </b-form-checkbox>
                        </b-form-group>
                    </b-col>
                </b-row>

                <!-- Capability Toggles -->
                <div class="bg-light p-3 rounded border mb-3">
                    <div class="d-flex justify-content-between align-items-center flex-wrap mb-2">
                        <div class="small font-weight-bold text-dark">Platform Capabilities &amp; Permissions</div>
                        <a
                            v-if="patSettingsUrl"
                            :href="patSettingsUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="btn btn-sm btn-outline-primary py-0 px-2 small font-weight-bold d-inline-flex align-items-center"
                            data-test="link-step1-azure-pat-settings"
                        >
                            <i class="bi bi-key-fill mr-1"></i>{{ patSettingsLabel }}
                            <i class="bi bi-box-arrow-up-right ml-1"></i>
                        </a>
                    </div>
                    <div class="d-flex flex-wrap">
                        <b-form-checkbox
                            v-model="form.supportsBacklog"
                            class="mr-4 mb-1"
                            switch
                            data-test="switch-supports-backlog"
                            @change="onCredentialChanged"
                        >
                            <span class="font-weight-bold small text-dark">
                                <i class="bi bi-kanban text-primary mr-1"></i>Supports Backlog & Sprints
                            </span>
                        </b-form-checkbox>
                        <b-form-checkbox
                            v-model="form.supportsRepos"
                            class="mb-1"
                            switch
                            data-test="switch-supports-repos"
                            @change="onCredentialChanged"
                        >
                            <span class="font-weight-bold small text-dark">
                                <i class="bi bi-git text-danger mr-1"></i>Supports Git Repositories & PRs
                            </span>
                        </b-form-checkbox>
                    </div>
                    <div v-if="!form.supportsBacklog && !form.supportsRepos" class="text-danger extra-small mt-1">
                        <i class="bi bi-exclamation-circle mr-1"></i>At least one capability must be enabled.
                    </div>
                    <div v-else class="text-muted extra-small mt-1">
                        VelocyTrack will allow this connection to be picked for the enabled capabilities.
                        <div v-if="isEditing" class="mt-2 pt-2 border-top text-muted" v-html="tokenHelpHtml"></div>
                    </div>
                </div>

                <!-- Primary Toggle -->
                <b-form-checkbox v-model="form.isPrimary" class="mb-3" data-test="chk-link-primary">
                    <span class="small font-weight-500 text-dark">
                        Set as primary {{ currentPlatformName }} connection for this account
                    </span>
                </b-form-checkbox>
            </div>

            </div>

            <!-- ==================== STEP 2: AUTHENTICATION & LIVE VERIFICATION ==================== -->
            <div v-if="currentStep === 2" data-test="step-authentication">
                <!-- Selected Platform & Organization Summary Pill -->
                <div class="d-flex justify-content-between align-items-center bg-light border rounded px-3 py-2 mb-3 small" data-test="step2-connection-summary">
                    <div class="d-flex align-items-center flex-wrap">
                        <span class="font-weight-bold text-dark mr-2">
                            <i class="bi bi-building mr-1 text-primary"></i>{{ currentPlatformName }}:
                            <code>{{ form.tenantSlug }}</code>
                        </span>
                        <b-badge v-if="form.supportsBacklog" variant="primary" class="mr-1 px-2 py-1">Backlog &amp; Sprints</b-badge>
                        <b-badge v-if="form.supportsRepos" variant="danger" class="mr-1 px-2 py-1">Git &amp; PRs</b-badge>
                    </div>
                    <b-button type="button" variant="link" size="sm" class="p-0 font-weight-bold" data-test="btn-change-details" @click="goToStep(1)">
                        <i class="bi bi-pencil-square mr-1"></i>Edit Details
                    </b-button>
                </div>

                <!-- 3. Authentication & Credentials -->
            <div class="mb-4">
                <label class="font-weight-bold text-dark small text-uppercase tracking-wider mb-2 d-block">
                    {{ isEditing ? 'Authentication & Access Token' : '3. Authentication & Access Token' }}
                </label>

                <!-- Stateless Execution & Token Security Notice Badge -->
                <div class="alert alert-light border small py-2 px-3 mb-3 d-flex align-items-start" data-test="badge-stateless-execution-notice">
                    <i class="bi bi-shield-check text-success mr-2 mt-1 fa-lg"></i>
                    <div>
                        <strong class="text-dark">Stateless Execution:</strong>
                        <div class="text-muted mt-1" style="line-height: 1.45;">
                            This platform processes pull requests, commits, and tickets on-the-fly in transient volatile memory. Your source code is never cloned, cached, or stored on our servers. Credentials are encrypted at rest with AES-256-GCM.
                        </div>
                    </div>
                </div>

                <div v-if="(form.platformType === 0 || form.platformType === 2) && !isEditing" class="mb-3">
                    <b-button variant="outline-primary" class="w-100 d-flex align-items-center justify-content-center" @click="startOAuthFlow" :disabled="isOAuthStarting" data-test="btn-connect-oauth">
                        <b-spinner small v-if="isOAuthStarting" class="mr-2"></b-spinner>
                        <template v-else>
                            <i v-if="form.platformType === 2" class="fab fa-github mr-2"></i>
                            <i v-else class="bi bi-microsoft mr-2"></i>
                            Connect securely with {{ form.platformType === 2 ? 'GitHub' : 'Azure DevOps' }} OAuth
                        </template>
                        <b-badge v-if="isViewOnlyMode" variant="warning" class="text-dark font-weight-bold ml-2 px-2 py-1" data-test="badge-oauth-pro-plan">
                            <i class="bi bi-star-fill mr-1"></i>PRO PLAN
                        </b-badge>
                    </b-button>
                    <div class="text-center mt-2 small text-muted">Or use a Personal Access Token (PAT) below</div>
                </div>

                <!-- Personal Access Token (PAT) input -->
                <b-form-group
                    label-for="link-pat"
                    class="mb-2"
                >
                    <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
                        <label for="link-pat" class="mb-0 font-weight-500">Personal Access Token (PAT)</label>
                        <a
                            v-if="patSettingsUrl"
                            :href="patSettingsUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="btn btn-sm btn-outline-primary py-0 px-2 small font-weight-bold d-inline-flex align-items-center"
                            data-test="link-azure-pat-settings"
                        >
                            <i class="bi bi-key-fill mr-1"></i>{{ patSettingsLabel }}
                            <i class="bi bi-box-arrow-up-right ml-1"></i>
                        </a>
                    </div>
                    <template v-if="false" #label>
                        <div class="d-flex justify-content-between align-items-center flex-wrap">
                            <span>Personal Access Token (PAT)</span>
                            <a
                                v-if="patSettingsUrl"
                                :href="patSettingsUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="small font-weight-bold text-primary d-inline-flex align-items-center"
                                data-test="link-azure-pat-settings"
                            >
                                <i class="bi bi-key mr-1"></i>{{ patSettingsLabel }}
                                <i class="bi bi-box-arrow-up-right ml-1"></i>
                            </a>
                        </div>
                    </template>
                    <b-input-group>
                        <b-form-input
                            id="link-pat"
                            :type="showPat ? 'text' : 'password'"
                            v-model.trim="form.pat"
                            :placeholder="isEditing ? (form.tokenHint ? '••••••••••••' + form.tokenHint + ' (Leave blank to keep existing token)' : '•••••••••••••••• (Leave blank to keep existing token)') : 'Paste Personal Access Token here'"
                            :required="!isEditing && !form.hasOAuth"
                            data-test="input-link-pat"
                            @input="onCredentialChanged"
                        ></b-form-input>
                        <b-input-group-append>
                            <b-button type="button" variant="outline-secondary" @click="showPat = !showPat" :title="showPat ? 'Hide PAT' : 'Show PAT'" :aria-label="showPat ? 'Hide PAT' : 'Show PAT'" data-test="btn-toggle-pat">
                                <i :class="showPat ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                    <div class="small text-muted mt-1" v-html="tokenHelpHtml"></div>
                </b-form-group>
            </div>

            <!-- 4. Live Verification Banner & Button -->
            <div class="mb-4">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <label class="font-weight-bold text-dark small text-uppercase tracking-wider mb-0">
                        Connection Test
                    </label>
                    <b-button
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        :disabled="!canVerify || isVerifying"
                        data-test="btn-test-connection"
                        :data-state="verifyState"
                        @click="verifyConnectionAsync"
                    >
                        <b-spinner v-if="isVerifying" small class="mr-1"></b-spinner>
                        <i v-else class="bi bi-shield-check mr-1"></i>
                        {{ isVerifying ? 'Testing Connection...' : 'Test Connection Live' }}
                    </b-button>
                </div>

                <!-- Pre-Test Least-Privilege & Verification Requirement Notice -->
                <div v-if="!verifyResult" class="alert alert-warning border-warning small py-2 px-3 mb-0 d-flex align-items-start" data-test="alert-pat-least-privilege-warning">
                    <i class="bi bi-shield-lock-fill text-warning mr-2 mt-1 fa-lg"></i>
                    <div>
                        <strong class="text-dark">Least-Privilege &amp; Live Verification Required:</strong>
                        <div class="text-dark mt-1" style="line-height: 1.45;">
                            Use a <strong>Custom Defined</strong> PAT (30&ndash;90 day expiry) scoped only to the required permissions above. Click <strong>Test Connection Live</strong> to verify connectivity and inspect token scopes before saving.
                        </div>
                    </div>
                </div>

                <!-- Verification Result Alert -->
                <b-alert
                    v-if="verifyResult"
                    show
                    :variant="verifyResult.success ? 'success' : 'danger'"
                    :data-test="verifyResult.success ? 'alert-verify-success' : 'alert-verify-danger'"
                    class="py-2 px-3 mb-0 d-flex align-items-center"
                >
                    <i :class="verifyResult.success ? 'bi bi-check-circle-fill text-success' : 'bi bi-exclamation-triangle-fill text-danger'" class="fa-lg mr-2"></i>
                    <div class="small">
                        <strong>{{ verifyResult.success ? 'Verification Succeeded' : 'Verification Failed' }}:</strong>
                        {{ (verifyResult.missingPermissions && verifyResult.missingPermissions.length) ? 'The Personal Access Token is missing required permission scope(s).' : verifyResult.message }}
                        <div v-if="verifyResult.missingPermissions && verifyResult.missingPermissions.length" class="mt-1">
                            <span class="font-weight-bold mr-1">Missing Scopes:</span>
                            <b-badge
                                v-for="(perm, idx) in verifyResult.missingPermissions"
                                :key="'mis-' + idx"
                                variant="danger"
                                class="mr-1 mb-1 px-2 py-1"
                                data-test="badge-missing-scope"
                            >
                                <i class="bi bi-x-circle mr-1"></i>{{ perm }}
                            </b-badge>
                            <a
                                v-if="patSettingsUrl"
                                :href="patSettingsUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="ml-1 font-weight-bold text-danger text-underline d-inline-flex align-items-center"
                                data-test="link-missing-scopes-pat-settings"
                            >
                                <i class="bi bi-key-fill mr-1"></i>{{ patSettingsLabel }}
                                <i class="bi bi-box-arrow-up-right ml-1"></i>
                            </a>
                        </div>
                        <div v-if="verifyResult.verifiedPermissions && verifyResult.verifiedPermissions.length" class="mt-1">
                            <span v-if="!verifyResult.success" class="font-weight-bold mr-1">Verified Scopes:</span>
                            <b-badge
                                v-for="(perm, idx) in verifyResult.verifiedPermissions"
                                :key="'ver-' + idx"
                                variant="success"
                                class="mr-1 mb-1 px-2 py-1"
                                data-test="badge-verified-scope"
                            >
                                <i class="bi bi-check2 mr-1"></i>{{ perm }}
                            </b-badge>
                            <b-badge
                                v-for="(optPerm, idx) in (verifyResult.optionalPermissions || [])"
                                :key="'opt-' + idx"
                                variant="warning"
                                class="text-dark mr-1 mb-1 px-2 py-1"
                                data-test="badge-optional-scope"
                                v-b-tooltip.hover
                                title="Code (Read & write) is optional. Without it, repositories and branches operate in read-only mode with direct deep links to Azure DevOps, and attempting to delete a branch will display a toast linking to Connection Settings."
                            >
                                <i class="bi bi-info-circle-fill mr-1"></i>{{ optPerm }}
                            </b-badge>
                        </div>
                    </div>
                </b-alert>

                <!-- Excessive Permissions Security Warning (only adds new scope warning info) -->
                <b-alert
                    v-if="verifyResult && verifyResult.hasExcessivePermissions"
                    show
                    variant="danger"
                    data-test="alert-verify-excessive-permissions"
                    class="py-2 px-3 mt-2 mb-0 border-danger"
                >
                    <div class="d-flex align-items-start">
                        <i class="bi bi-shield-fill-exclamation text-danger fa-lg mr-2 mt-1"></i>
                        <div class="small">
                            <div class="font-weight-bold text-uppercase mb-1" data-test="warning-heading">
                                High-Importance Security Warning — Excessive Token Permissions Detected
                            </div>
                            <div v-if="verifyResult.excessivePermissions && verifyResult.excessivePermissions.length" class="mb-1">
                                <span class="font-weight-bold mr-1">Unneeded Scopes Granted:</span>
                                <b-badge
                                    v-for="(perm, idx) in verifyResult.excessivePermissions"
                                    :key="'exc-' + idx"
                                    variant="danger"
                                    class="mr-1 mb-1 px-2 py-1"
                                    data-test="badge-excessive-scope"
                                >
                                    <i class="bi bi-exclamation-octagon mr-1"></i>{{ perm }}
                                </b-badge>
                            </div>
                            <div>
                                You can save this verified connection now, or replace the token with a Custom Defined PAT restricted to the required scopes above.
                            </div>
                        </div>
                    </div>
                </b-alert>
                <div v-if="!verifyResult && isEditing && form.isVerified && !form.pat" class="small text-success d-flex align-items-center mt-2">
                    <i class="bi bi-check-circle-fill mr-1"></i>
                    <span>This connection was verified{{ form.lastVerifiedAt ? ' on ' + formatDate(form.lastVerifiedAt) : '' }}. You can test it live again if needed.</span>
                </div>
            </div>
            </div>

            <!-- Actions Footer -->
            <div class="d-flex justify-content-between align-items-center pt-3 border-top">
                <b-button type="button" variant="secondary" @click="hide" data-test="btn-cancel-connection">
                    Cancel
                </b-button>
                <div class="d-flex align-items-center">
                    <b-button
                        v-if="currentStep === 1"
                        type="button"
                        variant="primary"
                        class="px-4 shadow-sm"
                        :disabled="!canProceedToStep2"
                        data-test="btn-next-step"
                        @click="goToStep(2)"
                    >
                        Next: Authentication <i class="bi bi-arrow-right ml-1"></i>
                    </b-button>
                    <template v-else>
                        <b-button
                            type="button"
                            variant="outline-secondary"
                            class="mr-2 px-3"
                            data-test="btn-prev-step"
                            @click="goToStep(1)"
                        >
                            <i class="bi bi-arrow-left mr-1"></i>Back
                        </b-button>
                        <b-button
                            type="submit"
                            variant="primary"
                            class="px-4 shadow-sm"
                            :disabled="!isFormValid || isSaving"
                            data-test="btn-save-connection"
                            :data-state="isSaving ? 'loading' : 'idle'"
                        >
                            <b-spinner v-if="isSaving" small class="mr-1"></b-spinner>
                            <i v-else class="bi bi-check2-circle mr-1"></i>
                            {{ isEditing ? 'Save Changes' : 'Save Platform Link' }}
                        </b-button>
                    </template>
                </div>
            </div>
        </b-form>
    </b-modal>
</template>

<script>
import mixinAccount from '../mixinAccount';
import mixinToast from '../mixinToast';

export default {
    name: 'ModalPlatformLink',
    mixins: [mixinAccount, mixinToast],
    props: {
        id: {
            type: String,
            default: 'modal-platform-link'
        }
    },
    data() {
        return {
            currentStep: 1,
            isEditing: false,
            isVerifying: false,
            isSaving: false,
            isOAuthStarting: false,
            showPat: false,
            useCustomName: false,
            verifyResult: null,
            form: {
                id: null,
                platformType: 0,
                name: '',
                tenantSlug: '',
                pat: '',
                supportsBacklog: true,
                supportsRepos: true,
                isPrimary: false,
                hasOAuth: false,
                isVerified: false,
                lastVerifiedAt: null,
                tokenHint: ''
            },
            platformOptions: [
                {
                    type: 0,
                    name: 'Azure DevOps',
                    subtitle: 'Backlog & Repos',
                    icon: 'icon-azure',
                    isAvailable: true
                },
                {
                    type: 2,
                    name: 'GitHub',
                    subtitle: 'Backlog, Repos & PRs',
                    icon: 'fab fa-github text-dark',
                    isAvailable: true
                },
                {
                    type: 3,
                    name: 'GitLab',
                    subtitle: 'Repos & CI',
                    icon: 'fab fa-gitlab text-warning',
                    isAvailable: false
                },
                {
                    type: 1,
                    name: 'Jira Software',
                    subtitle: 'Backlog Only',
                    icon: 'fab fa-jira text-info',
                    isAvailable: false
                }
            ]
        };
    },
    computed: {
        currentPlatformName() {
            const match = this.platformOptions.find(p => p.type === this.form.platformType);
            return match ? match.name : 'Platform';
        },
        platformIconClass() {
            switch (this.form.platformType) {
                case 0: return 'icon-azure';
                case 1: return 'fab fa-jira';
                case 2: return 'fab fa-github';
                case 3: return 'fab fa-gitlab';
                default: return 'fas fa-cloud';
            }
        },
        platformThemeColor() {
            switch (this.form.platformType) {
                case 0: return 'primary';
                case 1: return 'info';
                case 2: return 'dark';
                case 3: return 'warning';
                default: return 'secondary';
            }
        },
        tenantLabel() {
            switch (this.form.platformType) {
                case 0: return 'Organization Name (Slug)';
                case 1: return 'Atlassian Subdomain';
                case 2: return 'GitHub Organization / Account';
                case 3: return 'GitLab Group / Account';
                default: return 'Tenant / Organization Slug';
            }
        },
        tenantDescription() {
            switch (this.form.platformType) {
                case 0: return "From dev.azure.com/{organization}";
                case 1: return "From {subdomain}.atlassian.net";
                case 2: return "From github.com/{org-or-user}";
                case 3: return "From gitlab.com/{group-or-user}";
                default: return "The cloud service tenant identifier";
            }
        },
        tenantPlaceholder() {
            switch (this.form.platformType) {
                case 0: return "my-company-ado";
                case 1: return "company-jira";
                case 2: return "my-github-org";
                case 3: return "my-gitlab-group";
                default: return "slug";
            }
        },
        patSettingsUrl() {
            const orgSlug = encodeURIComponent((this.form.tenantSlug || '').trim() || 'your-org');
            switch (this.form.platformType) {
                case 0: return `https://dev.azure.com/${orgSlug}/_usersSettings/tokens`;
                case 1: return 'https://id.atlassian.com/manage-profile/security/api-tokens';
                case 2: return 'https://github.com/settings/tokens';
                case 3: return 'https://gitlab.com/-/user_settings/personal_access_tokens';
                default: return '';
            }
        },
        patSettingsLabel() {
            switch (this.form.platformType) {
                case 0: return 'Open Azure DevOps PAT Settings';
                case 1: return 'Open Atlassian API Tokens';
                case 2: return 'Open GitHub PAT Settings';
                case 3: return 'Open GitLab PAT Settings';
                default: return 'Open PAT Settings';
            }
        },
        tokenHelpHtml() {
            switch (this.form.platformType) {
                case 0: {
                    const orgSlug = encodeURIComponent((this.form.tenantSlug || '').trim() || 'your-org');
                    const adoUrl = `https://dev.azure.com/${orgSlug}/_usersSettings/tokens`;
                    const actionLabel = this.isEditing ? 'Edit / Manage token in Azure DevOps' : 'Create token in Azure DevOps';
                    const lines = [`Required Azure DevOps scopes (<a href="${adoUrl}" target="_blank" rel="noopener noreferrer" data-test="link-create-pat">${actionLabel} <i class="bi bi-box-arrow-up-right"></i></a>):`];
                    if (this.form.supportsBacklog) {
                        lines.push('&bull; <strong>Backlog:</strong> <code>Work Items (Read &amp; Write)</code>, <code>Project &amp; Team (Read)</code>');
                    }
                    if (this.form.supportsRepos) {
                        lines.push('&bull; <strong>Repositories:</strong> <code>Code (Read)</code> <span class="badge badge-warning text-dark ml-1 px-2 py-1" data-test="badge-optional-repo-write" title="Optional: Select \'Code (Read &amp; write)\' in Azure DevOps only if you want to delete branches or edit PRs directly from VelocyTrack (do not select Read, write, &amp; manage, Full, or Status). Without it, you can browse all branches and use deep links to manage them in Azure DevOps, and deletion actions will show a toast linking to Configuration."><i class="bi bi-info-circle-fill mr-1"></i>Code (Read &amp; write) Optional</span>');
                    }
                    if (!this.form.supportsBacklog && !this.form.supportsRepos) {
                        lines.push('&bull; <span class="text-warning"><i class="bi bi-exclamation-triangle mr-1"></i>Select at least one capability above.</span>');
                    }
                    lines.push('&bull; <strong>Identity:</strong> <code>User Profile (Read)</code>');
                    return lines.join('<br>');
                }
                case 1: {
                    const lines = ['Required Jira scopes (<a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" data-test="link-create-pat">Create token in Atlassian <i class="bi bi-box-arrow-up-right"></i></a>):'];
                    if (this.form.supportsBacklog) {
                        lines.push('&bull; <strong>Backlog Tracking:</strong> <code>read:jira-work</code>, <code>write:jira-work</code>');
                    }
                    lines.push('&bull; <strong>Identity:</strong> <code>read:jira-user</code>');
                    return lines.join('<br>');
                }
                case 2: {
                    const lines = ['Required GitHub scopes (<a href="https://github.com/settings/personal-access-tokens/new" target="_blank" rel="noopener noreferrer" data-test="link-create-pat">Create Fine-Grained Token in GitHub <i class="bi bi-box-arrow-up-right"></i></a>):'];
                    if (this.form.supportsBacklog) {
                        lines.push('&bull; <strong>Backlog:</strong> <code>Issues (Read &amp; write)</code>, <code>Projects (Read &amp; write)</code> (Optional for Projects v2)');
                    }
                    if (this.form.supportsRepos) {
                        lines.push('&bull; <strong>Repositories:</strong> <code>Pull requests (Read &amp; write)</code>, <code>Contents (Read-only)</code> <span class="badge badge-warning text-dark ml-1 px-2 py-1" data-test="badge-optional-repo-write" title="Optional: Select \'Contents (Read &amp; write)\' in GitHub only if you want to delete branches or push commits directly from VelocyTrack. Without it, pull requests, diffs, and branch browsing function in read-only mode."><i class="bi bi-info-circle-fill mr-1"></i>Contents (Read &amp; write) Optional</span>');
                    }
                    if (!this.form.supportsBacklog && !this.form.supportsRepos) {
                        lines.push('&bull; <span class="text-warning"><i class="bi bi-exclamation-triangle mr-1"></i>Select at least one capability above.</span>');
                    }
                    lines.push('&bull; <strong>Identity:</strong> <code>User Profile (Read)</code> or <code>read:user</code>');
                    return lines.join('<br>');
                }
                case 3: {
                    const lines = ['Required GitLab scopes (<a href="https://gitlab.com/-/user_settings/personal_access_tokens" target="_blank" rel="noopener noreferrer" data-test="link-create-pat">Create token in GitLab <i class="bi bi-box-arrow-up-right"></i></a>):'];
                    if (this.form.supportsRepos) {
                        lines.push('&bull; <strong>Repositories &amp; CI:</strong> <code>api</code>, <code>read_repository</code>, <code>write_repository</code>');
                    }
                    lines.push('&bull; <strong>Identity:</strong> <code>read_user</code>');
                    return lines.join('<br>');
                }
                default:
                    return 'Enter personal access token with read/write access.';
            }
        },
        verifyState() {
            if (this.isVerifying) return 'loading';
            if (!this.verifyResult) return 'idle';
            return this.verifyResult.success ? 'success' : 'error';
        },
        canVerify() {
            if (!this.form.tenantSlug) return false;
            if (!this.form.supportsBacklog && !this.form.supportsRepos) return false;
            if (this.isEditing) return true;
            return !!this.form.pat || this.form.hasOAuth;
        },
        canProceedToStep2() {
            if (!this.form.name || !this.form.tenantSlug) return false;
            if (!this.form.supportsBacklog && !this.form.supportsRepos) return false;
            return true;
        },
        isFormValid() {
            if (!this.form.name || !this.form.tenantSlug) return false;
            if (!this.form.supportsBacklog && !this.form.supportsRepos) return false;
            if (!this.isEditing && !this.form.pat && !this.form.hasOAuth) return false;
            if (!this.form.isVerified) return false;
            if (this.verifyResult && !this.verifyResult.success) return false;
            return true;
        }
    },
    watch: {
        useCustomName(val) {
            if (!val) {
                this.form.name = this.form.tenantSlug;
            }
        }
    },
    methods: {
        open(linkData = null, prefill = null) {
            this.resetForm();
            if (linkData) {
                this.isEditing = true;
                const name = linkData.name || '';
                const tenantSlug = linkData.tenantSlug || linkData.azureOrganization || '';
                this.useCustomName = !!(name && tenantSlug && name !== tenantSlug);
                this.form = {
                    id: linkData.id,
                    platformType: linkData.platformType ?? 0,
                    name: name,
                    tenantSlug: tenantSlug,
                    pat: '',
                    supportsBacklog: linkData.supportsBacklog ?? true,
                    supportsRepos: linkData.supportsRepos ?? true,
                    isPrimary: !!linkData.isPrimary,
                    hasOAuth: !!linkData.hasOAuth,
                    isVerified: !!linkData.isVerified,
                    lastVerifiedAt: linkData.lastVerifiedAt || null,
                tokenHint: linkData.tokenHint || ''
                };
            } else {
                this.isEditing = false;
                this.useCustomName = false;
                const platType = prefill?.platformType ?? 0;
                this.setPlatformType(platType);
                if (prefill?.tenantSlug) {
                    this.form.tenantSlug = prefill.tenantSlug;
                    this.form.name = prefill.tenantSlug;
                }
            }
            this.$bvModal.show(this.id);
        },
        hide() {
            this.$bvModal.hide(this.id);
        },
        onHidden() {
            this.resetForm();
            this.$emit('hidden');
        },
        resetForm() {
            this.currentStep = 1;
            this.isEditing = false;
            this.isVerifying = false;
            this.isSaving = false;
            this.isOAuthStarting = false;
            this.showPat = false;
            this.useCustomName = false;
            this.verifyResult = null;
            this.form = {
                id: null,
                platformType: 0,
                name: '',
                tenantSlug: '',
                pat: '',
                supportsBacklog: true,
                supportsRepos: true,
                isPrimary: false,
                hasOAuth: false,
                isVerified: false,
                lastVerifiedAt: null,
                tokenHint: ''
            };
        },
        goToStep(step) {
            if (step === 2 && !this.canProceedToStep2) return;
            this.currentStep = step;
        },
        onFormSubmit() {
            if (this.currentStep === 1) {
                if (this.canProceedToStep2) {
                    this.currentStep = 2;
                }
                return;
            }
            this.saveAsync();
        },
        setPlatformType(type) {
            this.form.platformType = type;
            this.verifyResult = null;
            this.form.isVerified = false;
            this.form.lastVerifiedAt = null;
            if (!this.useCustomName) {
                this.form.name = this.form.tenantSlug || '';
            } else if (!this.form.name || this.form.name.startsWith('My ')) {
                const match = this.platformOptions.find(p => p.type === type);
                this.form.name = 'My ' + (match ? match.name : 'Connection');
            }
            // Defaults per type
            switch (type) {
                case 0: // AzureDevOps
                    this.form.supportsBacklog = true;
                    this.form.supportsRepos = true;
                    break;
                case 1: // Jira
                    this.form.supportsBacklog = true;
                    this.form.supportsRepos = false;
                    break;
                case 2: // GitHub
                    this.form.supportsBacklog = true;
                    this.form.supportsRepos = true;
                    break;
                case 3: // GitLab
                    this.form.supportsBacklog = false;
                    this.form.supportsRepos = true;
                    break;
            }
        },
        onTenantSlugInput() {
            this.onCredentialChanged();
            if (!this.useCustomName) {
                this.form.name = this.form.tenantSlug;
            }
        },
        async startOAuthFlow() {
            if (this.isPersonalViewOnlyMode) {
                const planName = this.form.platformType === 2 ? 'GitHub' : 'Azure DevOps';
                this.toastUpgradeRequired('Pro Plan', `Connecting via ${planName} OAuth requires the Pro Plan.`);
                return;
            }
            this.isOAuthStarting = true;
            try {
                const platformType = this.form.platformType === 2 ? 'GitHub' : 'AzureDevOps';
                const response = await this.$http.get('/Config/ConnectOAuth', {
                    params: {
                        platformType,
                        tenantSlug: this.form.tenantSlug
                    }
                });
                if (response.data && response.data.authorizationUrl) {
                    window.location.href = response.data.authorizationUrl;
                }
            } catch (error) {
                console.error('Failed to start OAuth flow', error);
                const msg = (error.response && error.response.data && error.response.data.message) || 'Could not start OAuth flow. Please try again.';
                this.$bvToast.toast(msg, {
                    title: 'OAuth Error',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.isOAuthStarting = false;
            }
        },
        onCredentialChanged() {
            this.verifyResult = null;
            this.form.isVerified = false;
            this.form.lastVerifiedAt = null;
        },
        async verifyConnectionAsync() {
            if (!this.canVerify) return;
            this.isVerifying = true;
            this.verifyResult = null;

            try {
                let resp;
                if (this.isEditing && !this.form.pat) {
                    resp = await this.$http.post(`/Config/VerifyPlatformLink/${this.form.id}`);
                } else {
                    const draftPayload = {
                        platformType: this.form.platformType,
                        name: this.form.name || 'Connection',
                        tenantSlug: this.form.tenantSlug,
                        pat: this.form.pat ? this.form.pat : undefined,
                        supportsBacklog: this.form.supportsBacklog,
                        supportsRepos: this.form.supportsRepos
                    };
                    resp = await this.$http.post('/Config/VerifyPlatformLinkDraft', draftPayload);
                }
                this.verifyResult = resp.data;
                if (resp.data && resp.data.success) {
                    this.form.supportsBacklog = resp.data.supportsBacklog;
                    this.form.supportsRepos = resp.data.supportsRepos;
                    this.form.isVerified = true;
                    this.form.lastVerifiedAt = resp.data.verifiedAt || new Date().toISOString();
                } else {
                    this.form.isVerified = false;
                    this.form.lastVerifiedAt = null;
                }
            } catch (err) {
                const msg = err.response?.data?.message || err.message || 'Verification failed';
                this.verifyResult = {
                    success: false,
                    message: msg
                };
                this.form.isVerified = false;
                this.form.lastVerifiedAt = null;
            } finally {
                this.isVerifying = false;
            }
        },
        async saveAsync() {
            if (!this.isFormValid) return;
            this.isSaving = true;

            try {
                const isVerified = this.verifyResult
                    ? !!this.verifyResult.success
                    : (!this.form.pat ? !!this.form.isVerified : false);

                const lastVerifiedAt = isVerified
                    ? (this.verifyResult?.verifiedAt || this.form.lastVerifiedAt || new Date().toISOString())
                    : null;

                const payload = {
                    id: this.form.id || undefined,
                    platformType: this.form.platformType,
                    name: this.form.name,
                    tenantSlug: this.form.tenantSlug,
                    pat: this.form.pat ? this.form.pat : undefined,
                    supportsBacklog: this.form.supportsBacklog,
                    supportsRepos: this.form.supportsRepos,
                    isPrimary: this.form.isPrimary,
                    isVerified: isVerified,
                    lastVerifiedAt: lastVerifiedAt
                };

                const resp = await this.$http.post('/Config/SavePlatformLink', payload);
                this.$emit('saved', resp.data);
                this.hide();
            } catch (err) {
                const msg = err.response?.data?.message || err.message || 'Failed to save platform link';
                this.$bvToast.toast(msg, {
                    title: 'Save Failed',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.isSaving = false;
            }
        },
        formatDate(iso) {
            if (!iso) return '';
            try {
                return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
            } catch {
                return iso;
            }
        }
    }
};
</script>

<style scoped>
.platform-icon-circle {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
}
.platform-select-card {
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    background-color: var(--vt-card-bg, #fff);
    border-color: var(--vt-card-border, #dee2e6) !important;
}
.platform-select-card:hover:not(.is-disabled) {
    border-color: #0078d4 !important;
    background-color: rgba(0, 120, 212, 0.08);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.06);
}
.platform-select-card.is-selected {
    border-color: #0078d4 !important;
    background-color: rgba(0, 120, 212, 0.12);
    box-shadow: 0 0 0 1px #0078d4;
}
.platform-select-card.is-disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: rgba(0, 0, 0, 0.04);
}
[data-theme="dark"] .platform-select-card.is-disabled {
    background-color: rgba(255, 255, 255, 0.03);
}
.check-pill {
    position: absolute;
    top: 6px;
    right: 6px;
    font-size: 0.65rem;
    padding: 2px 5px;
}
.coming-soon-pill {
    position: absolute;
    top: 6px;
    right: 6px;
    font-size: 0.65rem;
    padding: 2px 5px;
}
.extra-small {
    font-size: 0.72rem;
}
.tracking-wider {
    letter-spacing: 0.05em;
}
.wizard-stepper {
    position: relative;
    padding: 0 32px;
}
.wizard-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 2;
    cursor: pointer;
}
.step-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: var(--vt-input-bg, #e9ecef);
    border: 1px solid var(--vt-border, #dee2e6);
    color: var(--vt-muted, #6c757d);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.85rem;
    transition: all 0.2s;
}
.wizard-step.active .step-icon {
    background-color: #0078d4 !important;
    border-color: #0078d4 !important;
    color: #fff !important;
    box-shadow: 0 0 0 3px rgba(0, 120, 212, 0.25);
}
.wizard-step.completed .step-icon {
    background-color: #28a745 !important;
    border-color: #28a745 !important;
    color: #fff !important;
}
.step-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--vt-muted, #6c757d);
    margin-top: 4px;
}
.wizard-step.active .step-label {
    color: #0078d4;
}
.wizard-step-line {
    flex-grow: 1;
    height: 3px;
    background-color: var(--vt-border, #e9ecef);
    margin: 0 12px;
    margin-bottom: 18px;
    transition: all 0.2s;
}
.wizard-step-line.completed {
    background-color: #28a745;
}
</style>
